import { chakra, type SystemStyleObject } from '@chakra-ui/react';
import { forwardRef } from 'react';
import { controlSize, type ControlSize } from './_shared';

const fieldBase: SystemStyleObject = {
  width: 'full',
  minWidth: 0,
  fontFamily: 'body',
  color: 'text.primary',
  bg: 'surface.default',
  borderWidth: '1px',
  borderColor: 'border.strong',
  borderRadius: 'md',
  transitionProperty: 'border-color, box-shadow, background-color',
  transitionDuration: 'fast',
  transitionTimingFunction: 'standard',
  outline: 'none',
  _placeholder: { color: 'text.muted' },
  _hover: { borderColor: 'border.strong' },
  _focusVisible: { borderColor: 'action.primary', boxShadow: 'focus' },
  _disabled: { opacity: 0.55, cursor: 'not-allowed', bg: 'surface.subtle' },
  '&[aria-invalid="true"]': {
    borderColor: 'status.danger',
    _focusVisible: { borderColor: 'status.danger', boxShadow: '0 0 0 3px rgba(179,22,15,0.28)' },
  },
};

const Input = chakra('input', { base: fieldBase });
const Textarea = chakra('textarea', {
  base: { ...fieldBase, resize: 'vertical', py: '2', lineHeight: 'normal' },
});

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'color'> {
  size?: ControlSize;
  /** Mark the field as invalid (sets aria-invalid + danger styling). */
  invalid?: boolean;
}

/** Single-line text field. Full states: hover / focus-visible / disabled / invalid. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { size = 'md', invalid, ...rest },
  ref,
) {
  return <Input ref={ref} aria-invalid={invalid || undefined} css={controlSize[size]} {...rest} />;
});

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'color'> {
  invalid?: boolean;
  rows?: number;
}

/** Multi-line text field. */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid, rows = 4, ...rest },
  ref,
) {
  return (
    <Textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      px="3.5"
      fontSize="md"
      {...rest}
    />
  );
});
